// SponsorCatalogPage — /sponsor/catalog
// PUBLIC read-only catalog browser: every course a sponsored child receives.
// No login, no enroll buttons, one CTA. The proof layer of the sponsor funnel.
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Search, ChevronDown, X, Loader2 } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import SEO from '@/components/layout/SEO'
import { cleanCourseText } from '@/utils/textClean'
import { useTranslation } from 'react-i18next';

const M = '#06060c'
const G = '#d4a843'
const BR = 'rgba(212,168,67,0.15)'
const CA = 'rgba(255,255,255,0.03)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const serif = "'Source Serif 4', 'Playfair Display', serif"

const GROUP_ICONS: Record<string, string> = {
  'Mathematics': '📐', 'Sciences': '🔬', 'English & Writing': '📝', 'Languages': '🌐',
  'Humanities': '📜', 'Social Sciences': '👥', 'Technology & Computing': '💻',
  'Arts & Performance': '🎨', 'Business & Finance': '💼', 'Life Skills & Wellbeing': '🧘',
  'Christian Studies': '✝️', 'Careers & Study Skills': '🧭', 'Astronomy & Space': '🔭',
  'Elite Academy': '👑', 'General': '📚', 'Bible Journey': '📖',
  'Electives & Practical Arts': '🌍', 'Faith Stream': '✝️',
}
const GROUP_ORDER = ['Elite Academy', 'Mathematics', 'Sciences', 'English & Writing', 'Languages',
  'Humanities', 'Social Sciences', 'Technology & Computing', 'Arts & Performance',
  'Business & Finance', 'Life Skills & Wellbeing', 'Christian Studies', 'Bible Journey',
  'Careers & Study Skills', 'Astronomy & Space', 'Electives & Practical Arts', 'General']

interface Course {
  id: string; name: string; description: string | null; target_age: number | null
  subject_group: string; curriculum_track: string | null
  module_count: number | null; lesson_count: number | null; source: string
}

type Stream = 'all' | 'standard' | 'elite' | 'faith' | 'community'

const isElite = (c: Course) => c.subject_group === 'Elite Academy' ||
  ['foundation', 'intermediate', 'advanced'].includes(c.curriculum_track || '')
const isFaith = (c: Course) => c.curriculum_track === 'faith'
const isCommunity = (c: Course) => c.source === 'community'

export default function SponsorCatalogPage() {
  const { t } = useTranslation();

  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [stream, setStream] = useState<Stream>('all')
  const [expanded, setExpanded] = useState<Set<string>>(new Set(GROUP_ORDER.slice(0, 3)))
  const [detail, setDetail] = useState<{ course: Course; modules: { title: string; lessons: string[] }[] } | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    supabase.from('sponsor_catalog' as any)
      .select('id, name, description, target_age, subject_group, curriculum_track, module_count, lesson_count, source')
      .limit(300)
      .then(({ data }: any) => {
        const mapped = ((data ?? []) as Course[]).filter(c =>
          !c.name?.toLowerCase().includes('pastoral ministry'))   // same exclusion as the solo catalog
        setCourses(mapped)
        setLoading(false)
      })
  }, [])

  async function openDetail(course: Course) {
    setDetailLoading(true)
    setDetail({ course, modules: [] })
    try {
      const { data } = await supabase.from('sponsor_catalog' as any)
        .select('module_plan')
        .eq('id', course.id)
        .maybeSingle()
      const plan = Array.isArray((data as any)?.module_plan) ? (data as any).module_plan : []
      setDetail({
        course,
        modules: plan.map((m: any, i: number) => ({
          title: m.title || `Module ${i + 1}`,
          lessons: (Array.isArray(m.lessons) ? m.lessons : []).map((l: any, li: number) =>
            typeof l === 'string' ? l : (l.title || `Lesson ${li + 1}`)),
        })),
      })
    } finally { setDetailLoading(false) }
  }

  const filtered = courses.filter(c => {
    const q = search.toLowerCase()
    const matchSearch = !q || c.name.toLowerCase().includes(q) || c.subject_group.toLowerCase().includes(q)
    const matchStream = stream === 'all' ? true
      : stream === 'elite' ? isElite(c)
      : stream === 'faith' ? isFaith(c)
      : stream === 'community' ? isCommunity(c)
      : !isElite(c) && !isFaith(c) && !isCommunity(c)
    return matchSearch && matchStream
  })

  // Bible Journey IDs encode the canonical sequence (course-1 ... course-9).
  // Sort by id for faith, alphabetically by name for all other groups.
  const sortItems = (g: string, items: Course[]) =>
    g === 'Bible Journey'
      ? [...items].sort((a, b) => a.id.localeCompare(b.id))
      : [...items].sort((a, b) => a.name.localeCompare(b.name))

  const groups = GROUP_ORDER
    .map(g => ({ group: g, items: sortItems(g, filtered.filter(c => c.subject_group === g)) }))
    .filter(g => g.items.length > 0)
    .concat(
      Object.entries(filtered.reduce((acc, c) => {
        if (!GROUP_ORDER.includes(c.subject_group)) (acc[c.subject_group] = acc[c.subject_group] || []).push(c)
        return acc
      }, {} as Record<string, Course[]>)).map(([group, items]) => ({ group, items: sortItems(group, items) }))
    )

  const toggle = (g: string) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(g) ? next.delete(g) : next.add(g)
    return next
  })

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO title="Course Catalog — What Sponsored Children Receive | Still Waters Academy"
        description="Browse every course a sponsored child receives: the full academy, Elite Academy, Bible Journey, and the practical electives library. Nothing held back." noindex />

      {/* Header */}
      <div style={{ padding: '56px 20px 32px', textAlign: 'center', borderBottom: `1px solid ${BR}` }}>
        <Link to="/sponsor" style={{ color: MU, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>← Back to sponsorship</Link>
        <h1 style={{ fontFamily: serif, fontSize: 'clamp(1.9rem, 5vw, 3rem)', fontWeight: 700, margin: '16px 0 10px', color: '#0ea5e9' }}>
          Every course a sponsored child receives
        </h1>
        <p style={{ color: MU, fontSize: 15, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
          {loading ? 'Loading catalog…' : `${courses.length} courses — Standard, Elite Academy, Bible Journey, and the practical electives. Nothing held back, nothing diluted.`}
        </p>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px 80px' }}>
        {/* Search + stream pills */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} color={MU} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('sponsor.search_courses_rhetoric_marine_biology_archery')}
              style={{ width: '100%', padding: '13px 16px 13px 42px', borderRadius: 14, border: `1px solid ${BR}`, background: CA, color: TX, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['all', 'standard', 'elite', 'faith', 'community'] as Stream[]).map(s => (
              <button key={s} onClick={() => setStream(s)}
                style={{ padding: '7px 16px', borderRadius: 999, border: `1px solid ${stream === s ? G : BR}`, background: stream === s ? 'rgba(212,168,67,0.12)' : 'transparent', color: stream === s ? G : MU, fontWeight: 700, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize' }}>
                {s === 'faith' ? '📖 Bible Journey' : s === 'community' ? '🌍 Electives' : s}
              </button>
            ))}
          </div>
        </div>

        {/* Groups */}
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: MU }}><Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 10px', display: 'block' }} /> Loading…</div>
        ) : groups.map(({ group, items }) => {
          const isOpen = expanded.has(group)
          return (
            <div key={group} style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 18, marginBottom: 12, overflow: 'hidden' }}>
              <button onClick={() => toggle(group)}
                style={{ width: '100%', padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 20 }}>{GROUP_ICONS[group] || '📚'}</span>
                  <span style={{ fontWeight: 800, fontSize: 14, color: TX }}>{group}</span>
                  {group === 'Electives & Practical Arts' && (
                    <span style={{ fontSize: 9, fontWeight: 800, color: G, border: `1px solid ${BR}`, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase' }}>{t('sponsor.free_for_everyone')}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: MU, fontSize: 12, fontWeight: 600 }}>{items.length}</span>
                  <ChevronDown size={16} color={MU} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                </div>
              </button>
              {isOpen && (
                <div style={{ borderTop: `1px solid ${BR}` }}>
                  {items.map(c => (
                    <button key={c.id} onClick={() => openDetail(c)}
                      style={{ width: '100%', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, background: 'transparent', border: 'none', borderBottom: `1px solid rgba(212,168,67,0.07)`, cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: TX, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: MU }}>
                          {c.target_age ? `Age ${c.target_age}+ · ` : ''}{c.module_count ?? '—'} modules · {c.lesson_count ?? '—'} lessons
                        </div>
                      </div>
                      {isCommunity(c) && <span style={{ fontSize: 9, fontWeight: 800, color: G, flexShrink: 0 }}>🌍 COMMUNITY</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        })}

        {/* The one CTA */}
        <div style={{ marginTop: 40, textAlign: 'center', padding: '28px 20px', background: 'rgba(212,168,67,0.06)', border: `1px solid ${BR}`, borderRadius: 20 }}>
          <p style={{ fontFamily: serif, fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>{t('sponsor.this_is_what_17_50_month')}</p>
          <p style={{ color: MU, fontSize: 13, margin: '0 0 18px' }}>{t('sponsor.you_pay_half_we_match_the')}</p>
          <Link to="/sponsor/give" style={{ display: 'inline-block', padding: '14px 40px', borderRadius: 999, background: G, color: M, fontWeight: 800, fontSize: 15, textDecoration: 'none' }}>
            💛 Sponsor a Child
          </Link>
        </div>
      </div>

      {/* Course detail modal — module/lesson drill-down, fetched per course */}
      {detail && (
        <div onClick={e => { if (e.target === e.currentTarget) setDetail(null) }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(6,6,12,0.8)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#0d0d15', border: `1px solid ${BR}`, borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '82vh', overflowY: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <h3 style={{ fontFamily: serif, fontSize: 20, fontWeight: 700, margin: 0, paddingRight: 12, color: '#0ea5e9' }}>{detail.course.name}</h3>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', padding: 0 }}><X size={20} /></button>
            </div>
            <p style={{ color: MU, fontSize: 12, margin: '0 0 18px' }}>
              {detail.course.subject_group}{detail.course.target_age ? ` · Age ${detail.course.target_age}+` : ''} · included in every sponsored seat
            </p>
            {detailLoading ? (
              <div style={{ padding: 30, textAlign: 'center', color: MU }}><Loader2 size={20} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} /> Loading syllabus…</div>
            ) : detail.modules.length === 0 ? (
              <p style={{ color: MU, fontSize: 13, fontStyle: 'italic' }}>{t('sponsor.syllabus_details_coming_soon')}</p>
            ) : detail.modules.map((m, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 800, color: G, margin: '0 0 6px' }}>Module {i + 1}: {cleanCourseText(m.title)}</p>
                {m.lessons.map((l, li) => (
                  <p key={li} style={{ fontSize: 12, color: TX, opacity: 0.75, margin: '3px 0 3px 12px' }}>· {cleanCourseText(l)}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}